/**
 * Safely loads and validates data from localStorage.
 *
 * @template T The expected type of the data.
 * @param {string} key The key to retrieve from localStorage.
 * @param {(data: any) => data is T} validator A type guard function to validate the parsed data.
 * @param {T} defaultValue The value to return if loading or validation fails.
 * @returns {T} The loaded and validated data, or the default value.
 */
export const loadFromLocalStorage = <T>(
  key: string,
  validator: (data: any) => data is T,
  defaultValue: T
): T => {
  // SSR Guard: localStorage is not available on the server.
  if (typeof window === "undefined") {
    return defaultValue;
  }

  try {
    const serializedState = localStorage.getItem(key);
    if (serializedState === null) {
      return defaultValue;
    }

    const parsedData = JSON.parse(serializedState);

    // Use the provided validator to ensure type safety.
    if (validator(parsedData)) {
      return parsedData;
    } else {
      console.warn(
        `[LocalStorage] Data for key "${key}" failed validation. Returning default value.`
      );
      return defaultValue;
    }
  } catch (err) {
    console.error(`[LocalStorage] Could not load state for key "${key}"`, err);
    return defaultValue;
  }
};

/**
 * Safely saves data to localStorage.
 *
 * @template T The type of the data to save.
 * @param {string} key The key to save the data under.
 * @param {T} value The data to save.
 */
export const saveToLocalStorage = <T>(key: string, value: T): void => {
  // SSR Guard
  if (typeof window === "undefined") {
    return;
  }

  try {
    const serializedState = JSON.stringify(value);
    localStorage.setItem(key, serializedState);
  } catch (err) {
    console.error(`[LocalStorage] Could not save state for key "${key}"`, err);
  }
};
